import { createFileRoute } from "@tanstack/react-router"

import { pageHead } from "@/lib/metadata"
import { getFaq } from "@/server/web"
import Link from "@/components/link"
import { Plus } from "lucide-react"

import { Button } from "@anysola/ui/button"
import { HelpFrame } from "@/components/help/help-frame"
import { StillStuck } from "@/components/help/still-stuck"
import { SiteShell } from "@/components/layout/site-shell"
import { JsonLd } from "@/components/seo/json-ld"
import { ACCOUNT_URL } from "@/lib/app-links"
import { summaryResetClass } from "@/lib/ui-classes"
import { cn } from "@anysola/ui/utils"

const faqSections: {
  category: string
  items: { question: string; answer: string }[]
}[] = [
  {
    category: "About Anysola",
    items: [
      {
        question: "What is Anysola?",
        answer:
          "Anysola builds AI apps for the small jobs that eat your day. This site is where you manage your plan, credits and support.",
      },
      {
        question: "Does one plan cover every Anysola app?",
        answer:
          "Yes. If an app or feature isn’t included in every plan, the app or the pricing page says so.",
      },
      {
        question: "What are Anysola credits?",
        answer:
          "Every plan includes a monthly allowance of credits, one balance that Anysola’s apps spend from as you use them. The allowance refreshes each month and unused credits don’t carry over. Your balance and history are on the Billing page of your dashboard.",
      },
      {
        question: "Where does Anysola use AI, and what can it see?",
        answer:
          "Our privacy notice lists where we use AI and what it can see. On this site, that’s the support assistant: it answers tickets first, says that it’s an AI, and hands you to a person whenever you ask.",
      },
    ],
  },
  {
    category: "Account & privacy",
    items: [
      {
        question: "How do I delete my account and data?",
        answer:
          "Open your account, go to Security, and delete it there. That also deletes your tickets, credits and launch-list signup, removes you from any team, and cancels any paid plan. There’s nothing separate to request.",
      },
      {
        question: "What happens to a ticket I remove from my dashboard?",
        answer:
          "Remove hides the ticket from your dashboard view. It does not delete the support record, which is kept for up to two years and then erased automatically. Support can still see it, and the removal itself is recorded.",
      },
    ],
  },
  {
    category: "Support tickets",
    items: [
      {
        question: "Can I reopen a ticket myself?",
        answer:
          "If it’s resolved, just reply to it. That reopens the conversation, and we’ll email you when we write back. A closed ticket stays closed: open a new one and mention the old ticket’s reference.",
      },
      {
        question: "I haven’t received an email reply. What now?",
        answer:
          "Every reply also appears in your dashboard, so check there first. If it’s there, look in your spam folder.",
      },
    ],
  },
  {
    category: "Beta programs",
    items: [
      {
        question: "How do I apply to a beta program?",
        answer:
          "Open Beta applications in your dashboard, pick an open program, and apply with a verified account. Applying doesn’t add you to the launch list.",
      },
      {
        question: "How will I know if I’ve been accepted?",
        answer:
          "You’ll get an email, and your invitation and access instructions will show up in your dashboard once a decision is made.",
      },
    ],
  },
  {
    category: "Gifts",
    items: [
      {
        question: "Do I need an account to claim a gift?",
        answer:
          "Not always. Gifts without a claim limit work without an account. Limited gifts need a signed-in account with a verified email.",
      },
      {
        question: "Can I get a gift I claimed on one device back on another?",
        answer:
          'Sign in and use "Save reward to your account" to attach a previously claimed anonymous reward without using up another claim.',
      },
    ],
  },
  {
    category: "Launch list",
    items: [
      {
        question: "Why do I need to sign in to join the launch list?",
        answer:
          "It ties the signup to a real account instead of any address typed into a form, so the list can’t be used to mail strangers.",
      },
      {
        question: "How do I leave the launch list?",
        answer:
          "Use the unsubscribe link in any email we send, or the leave button on your dashboard.",
      },
    ],
  },
]

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqSections.flatMap((section) =>
    section.items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    }))
  ),
}

function FaqPage() {
  // An empty help center is a dead end, so the link waits for the first
  // article. Unreadable isn't empty: the sections and the link stay, and
  // /help says what's wrong.
  const { hasArticles } = Route.useLoaderData()
  const empty = hasArticles === false

  return (
    <SiteShell>
      <JsonLd data={faqJsonLd} />
      <h1 className="sr-only">FAQ</h1>

      <HelpFrame current="faq" counts={empty ? new Map() : undefined}>
        <div className="grid gap-3">
          {faqSections.map((section) => {
            const id = `faq-${section.category.toLowerCase().replace(/[^a-z]+/g, "-")}`
            return (
              <section
                key={section.category}
                aria-labelledby={id}
                className="rounded-surface bg-card p-2"
              >
                <h2
                  id={id}
                  className="px-3 pt-2 pb-2 text-sm text-muted-foreground"
                >
                  {section.category}
                </h2>
                <div className="grid gap-0.5">
                  {section.items.map((item) => (
                    <details
                      key={item.question}
                      className="group rounded-xl transition-colors open:bg-surface-secondary/60 hover:bg-surface-secondary/60"
                    >
                      <summary
                        className={cn(
                          summaryResetClass,
                          "flex items-center justify-between gap-4 rounded-xl px-3 py-3 text-[0.9375rem] text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                        )}
                      >
                        {item.question}
                        <Plus
                          aria-hidden
                          className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-45"
                        />
                      </summary>
                      <p className="max-w-prose px-3 pb-4 text-sm leading-relaxed text-muted-foreground">
                        {item.answer}
                      </p>
                    </details>
                  ))}
                </div>
              </section>
            )
          })}
        </div>

        <StillStuck className="mt-3">
          {!empty && (
            <Button variant="secondary" render={<Link href="/help" />}>
              Search the help center
            </Button>
          )}
          <Button render={<Link href={`${ACCOUNT_URL}/support?new=1`} />}>
            Contact support
          </Button>
        </StillStuck>
      </HelpFrame>
    </SiteShell>
  )
}

export const Route = createFileRoute("/faq")({
  // Only re-read to decide whether the help center has anything worth
  // linking to (the service keeps it 300 s, the Next page's `revalidate`).
  loader: () => getFaq(),
  head: () =>
    pageHead({
      title: "FAQ",
      description:
        "Quick answers about accounts, plans, credits, tickets, betas and gifts.",
      path: "/faq",
      image: "/faq/opengraph-image",
    }),
  component: FaqPage,
})
